import React from 'react';
import PropTypes from 'prop-types';
import useAddress from '@afosto/queries/contact/useAddress';
import useContact from '@afosto/queries/contact/useContact';
import useFulfilmentLocation from '@afosto/queries/fulfilment/useFulfilmentLocation';
import useShipment from '@afosto/queries/fulfilment/useShipment';
import useShippingMethod from '@afosto/queries/fulfilment/useShippingMethod';
import { useHistory } from 'react-router-dom';
import { FormattedHTMLMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ItemCount from '../ItemCount';
import ShipmentStatus from '../ShipmentStatus';
import Crate from './Crate';
import SkusCount from './SkusCount';
import Icon from '@afosto/app-components-react/Icon';
import formatDate from '@afosto/utils/date/formatDate';
import getAddressString from '@afosto/utils/getAddressString';
import getFullName from '@afosto/utils/getFullName';
import translations from './translations';

const ShipmentCard = ({
  Container,
  ignoreMissingItems,
  shipment,
  SelectionComponent,
  onClick,
  showViewButton,
}) => {
  const history = useHistory();
  const { id, location: locationId, contact_id: contactId } = shipment || {};
  const { data: contact } = useContact(contactId, { enabled: !!contactId });
  const { data: location } = useFulfilmentLocation(locationId, { enabled: !!locationId });
  const { data: shipmentData } = useShipment(id);
  const { data: shippingMethod } = useShippingMethod(shipmentData?.method_id, {
    enabled: !!shipmentData?.method_id,
  });
  const addressing = shipmentData?.addressing ?? {};
  const destinationIsLocation = addressing.to?.entity_type === 'location';

  const toLocationId = destinationIsLocation ? addressing.to.entity_id : null;
  const { data: toLocation } = useFulfilmentLocation(toLocationId, { enabled: !!toLocationId });

  const addressId = destinationIsLocation ? toLocationId?.address_id : addressing.to?.entity_id;
  const { data: toAddress } = useAddress(addressId, { enabled: !!addressId });

  const items = ignoreMissingItems
    ? (shipmentData?.items ?? []).filter(item => !item.is_missing)
    : shipmentData?.items ?? [];
  const skus = [...new Set(items.map(item => item.sku))];
  const shipmentDate = shipment?.is_outbound ? shipment?.ship_at : shipment?.expected_at;

  return (
    <Container>
      <Box display="flex" flexWrap="nowrap" alignItems="center">
        {SelectionComponent && <SelectionComponent />}
        <Box display="flex" flexGrow={1}>
          <CardActionArea onClick={onClick}>
            <Box p={2} display="flex" alignItems="center">
              <Box flex={1}>
                <Box display="flex">
                  <Box flex={1}>
                    <Typography variant="subtitle1">
                      {shipment.number?.toUpperCase() || shipment.id}
                      {(shipment?.is_complete === false || shipment?.is_overcomplete) && (
                        <Icon
                          icon={['fal', 'flag']}
                          color="error"
                          size="small"
                          style={{ marginLeft: 8 }}
                        />
                      )}
                    </Typography>
                    {shipmentDate && (
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(shipmentDate)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Hidden implementation="js" mdUp>
                  {(contact || toAddress || toLocation) && (
                    <Box my={2}>
                      {contact && (
                        <Typography variant="body2" component="div">
                          {getFullName(contact)}
                        </Typography>
                      )}
                      {toLocation && (
                        <Typography variant="body2" component="div">
                          {toLocation.name}
                        </Typography>
                      )}
                      {toAddress && (
                        <Typography variant="body2" component="div">
                          {getAddressString(toAddress)}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {shippingMethod && (
                    <Box mt={2}>
                      <Typography variant="overline" color="textSecondary">
                        <FormattedHTMLMessage {...translations.properties.shippingMethod} />
                      </Typography>
                      <Typography variant="body2" color="textPrimary">
                        {shippingMethod?.name}
                      </Typography>
                    </Box>
                  )}
                  {shipment?.purchase_order_numbers?.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="overline" color="textSecondary">
                        <FormattedHTMLMessage {...translations.properties.purchaseOrderNumbers} />
                      </Typography>
                      <Typography variant="body2" color="textPrimary">
                        {shipment.purchase_order_numbers
                          .map(number => number?.toUpperCase())
                          .join(',')}
                      </Typography>
                    </Box>
                  )}
                  <Grid container style={{ marginTop: 8 }}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">
                        <FormattedHTMLMessage {...translations.properties.status} />
                      </Typography>
                      <ShipmentStatus shipment={shipment} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">
                        <FormattedHTMLMessage {...translations.properties.location} />
                      </Typography>
                      <Typography variant="body2" color="textPrimary">
                        {location?.name ?? '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container style={{ marginTop: 8 }}>
                    {shipment?.crate && (
                      <Grid item xs={4}>
                        <Crate crate={shipment.crate} />
                      </Grid>
                    )}
                    <Grid item xs={shipment?.crate ? 4 : 6}>
                      <ItemCount itemCount={items?.length ?? 0} />
                    </Grid>
                    <Grid item xs={shipment?.crate ? 4 : 6}>
                      <SkusCount skusCount={skus?.length ?? 0} />
                    </Grid>
                  </Grid>
                </Hidden>
              </Box>
              <Hidden implementation="js" smDown>
                {(contact || toAddress || toLocation) && (
                  <Box flex={1} px={2}>
                    {contact && (
                      <Typography variant="body2" component="div">
                        {getFullName(contact)}
                      </Typography>
                    )}
                    {toLocation && (
                      <Typography variant="body2" component="div">
                        {toLocation.name}
                      </Typography>
                    )}
                    {toAddress && (
                      <Typography variant="body2" component="div">
                        {getAddressString(toAddress)}
                      </Typography>
                    )}
                  </Box>
                )}
                {shipment?.crate && (
                  <Box px={2} pb={2}>
                    <Crate crate={shipment.crate} />
                  </Box>
                )}
                <Box px={2} pb={2}>
                  <ItemCount itemCount={items?.length ?? 0} />
                </Box>
                <Box px={2} pb={2}>
                  <SkusCount skusCount={skus?.length ?? 0} />
                </Box>
                <Box px={2} pb={2}>
                  <Typography variant="overline" color="textSecondary">
                    <FormattedHTMLMessage {...translations.properties.status} />
                  </Typography>
                  <ShipmentStatus shipment={shipment} />
                </Box>
              </Hidden>
            </Box>
          </CardActionArea>
          {showViewButton && (
            <Box mx={1} pt={2}>
              <IconButton onClick={() => history.push(`/shipments/${shipment.id}`)}>
                <Icon icon={['fal', 'eye']} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

ShipmentCard.propTypes = {
  Container: PropTypes.elementType,
  ignoreMissingItems: PropTypes.bool,
  shipment: PropTypes.object.isRequired,
  SelectionComponent: PropTypes.elementType,
  onClick: PropTypes.func,
  showViewButton: PropTypes.bool,
};

ShipmentCard.defaultProps = {
  Container: Card,
  ignoreMissingItems: false,
  SelectionComponent: undefined,
  onClick: undefined,
  showViewButton: false,
};

export default ShipmentCard;
